"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useInView } from "framer-motion";
import { useAnimationConfig } from "@/context/AnimationContext";
import { Palette, ArrowUp, Zap, ZapOff, BookOpen, BookText } from "lucide-react";
import SpeedDial from "@/components/animata/fabs/speed-dial";
import { playClack, playTick } from "@/utils/audio";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";
import { useEmberTheme } from "@/hooks/useEmberTheme";
import { useSyncExternalStore } from "react";
import Footer3D from "@/components/Footer3D";
export default function Footer(t0) {
  const $ = _c(44);
  const {
    dict,
    className: t1,
    overlay: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const overlay = t2 === undefined ? false : t2;
  const footerRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      margin: "200px 0px 200px 0px"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const isInView = useInView(footerRef, t3);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      margin: "0px"
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isFooterVisible = useInView(footerRef, t4);
  const mounted = useSyncExternalStore(_temp2, _temp3, _temp4);
  const isEmber = useEmberTheme();
  const isNeumorphic = useNeumorphicTheme();
  const {
    isAnimationsDisabled,
    toggleAnimations,
    isReaderMode,
    toggleReaderMode
  } = useAnimationConfig();
  let t5;
  if ($[2] !== dict?.copyright) {
    const currentYear = new Date().getFullYear();
    t5 = (dict?.copyright || "\xA9 {year} SAMYBIT // WITH NEXT.JS & BRUTALISM.").replace("{year}", currentYear.toString());
    $[2] = dict?.copyright;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const copyrightText = t5;
  const cycleTheme = _temp5;
  const scrollToTop = _temp6;
  let t6;
  if ($[4] !== dict?.backToTop || $[5] !== dict?.closeActions || $[6] !== dict?.cycleTheme || $[7] !== dict?.disableMotion || $[8] !== dict?.enableMotion || $[9] !== dict?.exitTextMode || $[10] !== dict?.openActions || $[11] !== dict?.textOnlyMode || $[12] !== isAnimationsDisabled || $[13] !== isEmber || $[14] !== isFooterVisible || $[15] !== isNeumorphic || $[16] !== isReaderMode || $[17] !== toggleAnimations || $[18] !== toggleReaderMode) {
    t6 = isFooterVisible && <div className="fixed z-50 pointer-events-auto transition-all duration-300 bottom-8 right-8 md:bottom-10 md:right-10"><SpeedDial direction="up" triggerLabel={dict?.openActions || "Open Actions"} closeLabel={dict?.closeActions || "Close Actions"} className={isNeumorphic ? "[&>button]:bg-[#e0e5ec] [&>button]:text-[#4b5563] [&>button]:border-transparent [&>button]:shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] [&>ul>li>button]:bg-[#e0e5ec] [&>ul>li>button]:text-[#4b5563] [&>ul>li>button]:border-transparent [&>ul>li>button]:shadow-[4px_4px_8px_rgba(163,177,198,0.6),_-4px_-4px_8px_rgba(255,255,255,0.5)]" : isEmber ? "[&>button]:bg-[#1A1716] [&>button]:text-[#FF4F00] [&>button]:border-[#FF4F00] [&>button]:shadow-[4px_4px_0px_#FF4F00] [&>ul>li>button]:bg-[#1A1716] [&>ul>li>button]:text-[#FF4F00] [&>ul>li>button]:border-[#FF4F00] [&>ul>li>button]:shadow-[2px_2px_0px_#FF4F00]" : "[&_button]:rounded-none [&>button]:bg-white [&>button]:text-black [&>button]:border-2 [&>button]:border-black [&>button]:shadow-[4px_4px_0px_rgba(255,255,255,0.3)] [&>ul>li>button]:bg-white [&>ul>li>button]:text-black [&>ul>li>button]:border-2 [&>ul>li>button]:border-black [&>ul>li>button]:shadow-[2px_2px_0px_rgba(255,255,255,0.3)]"} actionButtons={[{
        key: "theme",
        label: dict?.cycleTheme || "Cycle Theme",
        icon: <Palette size={20} />,
        action: cycleTheme
      }, {
        key: "animations",
        label: isAnimationsDisabled ? dict?.enableMotion || "Enable Motion" : dict?.disableMotion || "Disable Motion",
        icon: isAnimationsDisabled ? <ZapOff size={20} /> : <Zap size={20} />,
        action: toggleAnimations
      }, {
        key: "reader",
        label: isReaderMode ? dict?.exitTextMode || "Exit Text Mode" : dict?.textOnlyMode || "Text-Only Mode",
        icon: isReaderMode ? <BookText size={20} /> : <BookOpen size={20} />,
        action: () => {
          playTick();
          toggleReaderMode();
        }
      }, {
        key: "top",
        label: dict?.backToTop || "Back to Top",
        icon: <ArrowUp size={20} />,
        action: scrollToTop
      }]} /></div>;
    $[4] = dict?.backToTop;
    $[5] = dict?.closeActions;
    $[6] = dict?.cycleTheme;
    $[7] = dict?.disableMotion;
    $[8] = dict?.enableMotion;
    $[9] = dict?.exitTextMode;
    $[10] = dict?.openActions;
    $[11] = dict?.textOnlyMode;
    $[12] = isAnimationsDisabled;
    $[13] = isEmber;
    $[14] = isFooterVisible;
    $[15] = isNeumorphic;
    $[16] = isReaderMode;
    $[17] = toggleAnimations;
    $[18] = toggleReaderMode;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const fab = t6;
  const t7 = overlay ? `w-full py-3.5 px-6 border-t-4 border-black/80 bg-black/85 backdrop-blur-md text-white transition-colors duration-300 ${className}` : `relative min-h-[20vh] flex items-center justify-center overflow-hidden border-t-8 border-black py-6 px-6 md:py-8 md:px-12 transition-colors duration-300 ${isNeumorphic ? "bg-white" : "bg-black"} ${className}`;
  let t8;
  if ($[20] !== isEmber || $[21] !== isInView || $[22] !== isNeumorphic || $[23] !== overlay) {
    t8 = !overlay && <div className="absolute inset-0 z-0 pointer-events-none"><Footer3D isInView={isInView} isEmber={isEmber} isNeumorphic={isNeumorphic} /></div>;
    $[20] = isEmber;
    $[21] = isInView;
    $[22] = isNeumorphic;
    $[23] = overlay;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = overlay ? "relative z-10 w-full max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left rtl:sm:text-right pointer-events-auto" : "relative z-10 text-center flex flex-col items-center justify-center gap-6 pointer-events-none";
  const t10 = `font-bold uppercase ${overlay ? isNeumorphic ? "text-black bg-white/90 px-3 py-1 text-xs sm:text-sm" : "text-white/90 text-xs sm:text-sm tracking-wider" : "text-black bg-white px-3 py-1 text-[clamp(0.75rem,3.5vw,1.25rem)]"}`;
  let t11;
  if ($[25] !== copyrightText || $[26] !== t10) {
    t11 = <p className={t10} suppressHydrationWarning={true}>{copyrightText}</p>;
    $[25] = copyrightText;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => {
      e.preventDefault();
      scrollToTop();
    };
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = `font-bold uppercase border-b-2 sm:border-b-4 pb-0.5 transition-colors pointer-events-auto cursor-pointer ${overlay ? isNeumorphic ? "border-black text-black hover:bg-black hover:text-white px-2 text-sm sm:text-base" : isEmber ? "border-[#FF4F00] text-[#FF4F00] hover:bg-[#FF4F00] hover:text-[#1A1716] px-2 text-sm sm:text-base" : "border-white text-white hover:bg-white hover:text-black px-2 text-sm sm:text-base" : `text-lg border-b-4 ${isNeumorphic ? "border-black text-black hover:bg-black hover:text-white" : isEmber ? "border-[#FF4F00] text-[#FF4F00] hover:bg-[#FF4F00] hover:text-[#1A1716]" : "border-white text-white hover:bg-white hover:text-black"}`}`;
  const t14 = dict?.backToTop || "\u2191 Back to top";
  let t15;
  if ($[29] !== t13 || $[30] !== t14) {
    t15 = <a href="#" onClick={t12} className={t13}>{t14}</a>;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t11 || $[33] !== t15 || $[34] !== t9) {
    t16 = <div className={t9}>{t11}{t15}</div>;
    $[32] = t11;
    $[33] = t15;
    $[34] = t9;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== fab || $[37] !== mounted) {
    t17 = mounted && typeof document !== "undefined" && createPortal(fab, document.body);
    $[36] = fab;
    $[37] = mounted;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17 || $[41] !== t7 || $[42] !== t8) {
    t18 = <footer ref={footerRef} className={t7}>{t8}{t16}{t17}</footer>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t7;
    $[42] = t8;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}
function _temp6() {
  playTick();
  window.scrollTo({
    top: 0,
    behavior: "smooth"
  });
}
function _temp5() {
  playClack();
  const html = document.documentElement;
  if (html.classList.contains("invert-theme")) {
    html.classList.remove("invert-theme");
    html.classList.add("theme-color");
  } else {
    if (html.classList.contains("theme-color")) {
      html.classList.remove("theme-color");
      html.classList.add("theme-neumorphic");
    } else {
      if (html.classList.contains("theme-neumorphic")) {
        html.classList.remove("theme-neumorphic");
      } else {
        html.classList.add("invert-theme");
      }
    }
  }
}
function _temp4() {
  return false;
}
function _temp3() {
  return true;
}
function _temp2() {
  return _temp;
}
function _temp() {}